import type {
  CyclePregnancyStatusAggregate,
  CyclePregnancyStatusRecord,
  PatientPregnancyStands,
} from "@src/packages/homepage/pregnancy/useCyclePregnancyStatus";
import { toLocalDateString } from "@src/packages/utils/date";
/**
 * Test-fixture active cycle. Drives the in-care preview on the home
 * page so designers and the visual-evidence capture routes can see what an
 * active cycle home looks like without having a real cycle row in the backend.
 *
 * Enabled with `?dev_force_active_cycle=true` on a `__DEV__` build.
 * Specific review states can be selected with `?dev_active_cycle_scenario=...`.
 * Persisted in sessionStorage for the tab. Same pattern as the other
 * dev overrides.
 *
 * The mock describes an egg-freezing cycle that started 10 days ago,
 * currently mid-stimulation (day 7 of protocol). Other consumers that
 * read cycle data (CareScreen, CycleDetailScreen, TreatmentDashboard,
 * ActiveCycleHero) all benefit from the same mock since it short-
 * circuits the two underlying hooks (`useActiveCycle`,
 * `useCyclePhases`).
 */
import type { Cycle, Phase } from "../hooks/useCycles";

const STORAGE_KEY = "__moon_dev_force_active_cycle";
const SCENARIO_STORAGE_KEY = "__moon_dev_active_cycle_scenario";

export type DevActiveCycleScenario =
  | "default"
  | "missing_where_when"
  // Egg retrieval started two days ago and is still awaiting its recorded
  // result; this is the Cycle Detail reproduction for issue #1557.
  | "retrieval_pending"
  // Post-transfer two-week wait (beta pending). Produces an IVF cycle whose
  // stim through transfer steps are complete and whose luteal phase is open, so
  // the homepage/care hero resolves to the beta-wait state instead of stim.
  | "waiting_beta"
  // Post-transfer wait with no beta date yet; exercises the scheduling CTA.
  | "waiting_beta_pending"
  // Cycle Detail two-week-wait layout fixture (from #1951).
  | "waiting_phase"
  // Sperm freezing: the sample was given yesterday and the semen analysis has
  // not come back, so Home resolves to the results wait (#3665).
  | "sperm_results_pending"
  // The same cycle once the analysis figures are typed in on the analysis
  // step, still open because the freezing comes after (#3681).
  | "sperm_results_entered"
  // Sperm freezing before the sample is given: screening and collection booked,
  // or nothing booked yet. Home resolves to the collection-day Home (#3703).
  | "sperm_collection_booked"
  | "sperm_collection_unbooked"
  // Frozen embryo transfer in lining preparation, with dated lining scans,
  // and the transfer either booked or not yet dated (#3703).
  | "fet_lining_prep"
  | "fet_transfer_unbooked"
  // IVF between stimulation and the wait: the retrieval booked after the
  // trigger, embryo development day 3, and transfer day.
  | "ivf_retrieval_booked"
  | "ivf_embryo_development"
  | "ivf_transfer_day"
  // IVF on the morning of the retrieval, before anything records it.
  | "ivf_retrieval_day"
  // IUI on insemination day: stimulation and the sample done, the
  // insemination booked for today.
  | "iui_insemination_day";

export type DevCycleDetailOutcome =
  "pregnancy" | "no_pregnancy" | "miscarriage" | "cancelled" | "other";

type DevOutcomeSource = "provider" | "patient" | "partner" | "system";
type DevOutcomeVerification = "unverified" | "verified" | "refuted";
type DevOutcomeResult = "pregnancy" | "no_pregnancy";

/**
 * Dev-only post-cycle fixture for the Cycle Detail close-out dialog.
 *
 * The route uses this independently from the active-cycle fixture so the
 * post-cycle handoff can be reviewed on the real product surface without
 * requiring a closed cycle in a test account.
 */
export const DEV_CYCLE_DETAIL_OUTCOME_OVERRIDE: DevCycleDetailOutcome | null =
  (() => {
    if (!__DEV__ || typeof window === "undefined") return null;
    const outcome = new URLSearchParams(window.location.search).get(
      "dev_cycle_detail_outcome",
    );
    if (
      outcome === "pregnancy" ||
      outcome === "no_pregnancy" ||
      outcome === "miscarriage" ||
      outcome === "cancelled" ||
      outcome === "other"
    ) {
      return outcome;
    }
    return null;
  })();

function isKnownScenario(
  value: string | null | undefined,
): value is Exclude<DevActiveCycleScenario, "default"> {
  return (
    value === "missing_where_when" ||
    value === "retrieval_pending" ||
    value === "waiting_beta" ||
    value === "waiting_beta_pending" ||
    value === "waiting_phase" ||
    value === "sperm_results_pending" ||
    value === "sperm_results_entered" ||
    value === "sperm_collection_booked" ||
    value === "sperm_collection_unbooked" ||
    value === "fet_lining_prep" ||
    value === "fet_transfer_unbooked" ||
    value === "ivf_retrieval_booked" ||
    value === "ivf_embryo_development" ||
    value === "ivf_transfer_day" ||
    value === "ivf_retrieval_day" ||
    value === "iui_insemination_day"
  );
}

function getDevActiveCycleScenario(): DevActiveCycleScenario {
  if (!__DEV__ || typeof window === "undefined") return "default";
  const fromUrl = new URLSearchParams(window.location.search).get(
    "dev_active_cycle_scenario",
  );
  if (isKnownScenario(fromUrl)) {
    try {
      window.sessionStorage?.setItem(SCENARIO_STORAGE_KEY, fromUrl);
    } catch {
      /* sessionStorage may be disabled */
    }
    return fromUrl;
  }
  if (fromUrl === "default") {
    try {
      window.sessionStorage?.removeItem(SCENARIO_STORAGE_KEY);
    } catch {
      /* sessionStorage may be disabled */
    }
    return "default";
  }
  try {
    const stored = window.sessionStorage?.getItem(SCENARIO_STORAGE_KEY);
    return isKnownScenario(stored) ? stored : "default";
  } catch {
    return "default";
  }
}

export const DEV_ACTIVE_CYCLE_OVERRIDE: boolean = (() => {
  if (!__DEV__ || typeof window === "undefined") return false;
  const fromUrl = new URLSearchParams(window.location.search).get(
    "dev_force_active_cycle",
  );
  if (fromUrl === "true" || fromUrl === "1") {
    try {
      window.sessionStorage?.setItem(STORAGE_KEY, "1");
    } catch {
      /* sessionStorage may be disabled */
    }
    return true;
  }
  if (fromUrl === "false" || fromUrl === "0") {
    try {
      window.sessionStorage?.removeItem(STORAGE_KEY);
    } catch {
      /* sessionStorage may be disabled */
    }
    return false;
  }
  try {
    return window.sessionStorage?.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
})();

const DEV_CARE_CONNECTIONS_CYCLE = (() => {
  if (!__DEV__ || typeof window === "undefined") return false;
  return (
    new URLSearchParams(window.location.search).get(
      "dev_care_connections_cycle",
    ) === "1"
  );
})();

/**
 * An explicit false value is useful for authenticated visual routes that need
 * to exercise the no-cycle surface even when the provisioned review account
 * has an open cycle in the API.
 */
export const DEV_FORCE_NO_ACTIVE_CYCLE: boolean = (() => {
  if (!__DEV__ || typeof window === "undefined") return false;
  const value = new URLSearchParams(window.location.search).get(
    "dev_force_active_cycle",
  );
  return value === "false" || value === "0";
})();

/**
 * The same escape hatch as `DEV_FORCE_NO_ACTIVE_CYCLE`, for the cycle *history*
 * rather than the open cycle.
 *
 * `dev_force_active_cycle=false` only empties the active cycle, so Cycle Detail's
 * empty state is still decided by the review account's closed cycles, and that
 * account accumulates them: one closed cycle carrying any recorded outcome sends
 * the screen to the post-cycle close-out instead of the first-time placeholder,
 * and the "Add past cycle" affordance lives only on the placeholder. A visual
 * route that needs the placeholder cannot depend on an account nobody resets.
 */
export const DEV_FORCE_NO_PAST_CYCLES: boolean = (() => {
  if (!__DEV__ || typeof window === "undefined") return false;
  const value = new URLSearchParams(window.location.search).get(
    "dev_force_past_cycles",
  );
  return value === "false" || value === "0";
})();

export const DEV_ACTIVE_CYCLE_SCENARIO = getDevActiveCycleScenario();

/**
 * Optional beta-wait day override for route-level visual review.
 * `?dev_wait_day=1|8|12` uses a fourteen-day beta window while moving the
 * transfer date so the Home reassurance thresholds can be reviewed.
 */
export const DEV_WAIT_DAYS_AFTER_TRANSFER_OVERRIDE: number | null = (() => {
  if (!__DEV__ || typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("dev_wait_day");
  if (!raw) return null;
  const days = Number(raw);
  return Number.isInteger(days) && days >= 0 && days <= 14 ? days : null;
})();

const CYCLE_TYPE_STORAGE_KEY = "__moon_dev_cycle_type";

const DEV_CYCLE_TYPES = [
  "ivf",
  "icsi",
  "iui",
  "fet",
  "oi",
  "egg_freezing",
  "embryo_freezing",
  "sperm_freezing",
] as const;

export type DevCycleTypeOverride = (typeof DEV_CYCLE_TYPES)[number];

function isDevCycleType(value: string | null): value is DevCycleTypeOverride {
  return !!value && (DEV_CYCLE_TYPES as readonly string[]).includes(value);
}

/**
 * Optional `?dev_cycleType=` override so the treatment hub can be reviewed for
 * any cycle type (the mock cycle is otherwise egg-freezing). It only reshapes
 * the step list / copy via `getStepsForPerspective`; the mock phases stay
 * generic. Persisted in sessionStorage for the tab like the scenario.
 */
function getDevCycleTypeOverride(): DevCycleTypeOverride | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  const fromUrl = new URLSearchParams(window.location.search).get(
    "dev_cycleType",
  );
  if (isDevCycleType(fromUrl)) {
    try {
      window.sessionStorage?.setItem(CYCLE_TYPE_STORAGE_KEY, fromUrl);
    } catch {
      /* sessionStorage may be disabled */
    }
    return fromUrl;
  }
  if (fromUrl === "default" || fromUrl === "reset") {
    try {
      window.sessionStorage?.removeItem(CYCLE_TYPE_STORAGE_KEY);
    } catch {
      /* sessionStorage may be disabled */
    }
    return null;
  }
  try {
    const stored = window.sessionStorage?.getItem(CYCLE_TYPE_STORAGE_KEY);
    return isDevCycleType(stored) ? stored : null;
  } catch {
    return null;
  }
}

export const DEV_CYCLE_TYPE_OVERRIDE = getDevCycleTypeOverride();

function getDevOutcomeField<T extends string>(
  key: string,
  values: readonly T[],
): T | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  const value = new URLSearchParams(window.location.search).get(key);
  return value && (values as readonly string[]).includes(value)
    ? (value as T)
    : null;
}

/** Optional provenance fields for a deterministic post-result Home fixture. */
export const DEV_OUTCOME_SOURCE_OVERRIDE: DevOutcomeSource | null =
  getDevOutcomeField("dev_outcome_source", [
    "provider",
    "patient",
    "partner",
    "system",
  ] as const);
export const DEV_OUTCOME_VERIFICATION_OVERRIDE: DevOutcomeVerification | null =
  getDevOutcomeField("dev_outcome_verification", [
    "unverified",
    "verified",
    "refuted",
  ] as const);
/**
 * `?dev_pregnancy_stands=continuing|ended|birth` and `?dev_pregnancy_weeks=8`
 * put the fixture outcome into a state a patient could only otherwise reach by
 * saving against a real cycle.
 *
 * The card only says "still pregnant" once she has given a week — nothing but the
 * update step writes one — so without these the reviewable states are the dialog
 * and nothing else. Read-only fixtures, exactly like the provenance overrides
 * above; they change what the mock outcome carries, never what a write does.
 */
export const DEV_PREGNANCY_STANDS_OVERRIDE: PatientPregnancyStands | null =
  getDevOutcomeField("dev_pregnancy_stands", [
    "continuing",
    "ended",
    "birth",
  ] as const);

export const DEV_PREGNANCY_WEEKS_OVERRIDE: number | null = (() => {
  if (!__DEV__ || typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get(
    "dev_pregnancy_weeks",
  );
  const parsed = raw ? Number.parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 45 ? parsed : null;
})();

/**
 * `?dev_pregnancy_babies=2` — how many babies the recorded birth says.
 *
 * A birth reads differently in the plural, and the fixture had no way to say so:
 * it forced `live_birth` with no count, so the plural copy had no reviewable
 * state. The bounds are the update step's own (`MoonNumericField`, 1 to 10), so
 * the fixture cannot describe a birth the product could not record.
 */
export const DEV_PREGNANCY_BABIES_OVERRIDE: number | null = (() => {
  if (!__DEV__ || typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get(
    "dev_pregnancy_babies",
  );
  const parsed = raw ? Number.parseInt(raw, 10) : NaN;
  return Number.isFinite(parsed) && parsed >= 1 && parsed <= 10 ? parsed : null;
})();

/**
 * `?dev_pregnancy_history=corrected` — she said the pregnancy was continuing and
 * later corrected herself.
 *
 * A second version is the only shape the read has for more than one row: the API
 * appends rather than overwrites, so the record it already held comes back with
 * `recordStatus: "corrected"` while the new head names it in
 * `supersedesStatusId` and carries the `correctionReason` the write sent. The
 * head also dates the event to a month rather than a day, which is the other
 * thing a version can differ by and the only way `eventDatePrecision` reaches
 * the read at all.
 */
export const DEV_PREGNANCY_HISTORY_OVERRIDE: "corrected" | null =
  getDevOutcomeField("dev_pregnancy_history", ["corrected"] as const);

/**
 * `?dev_pregnancy_resolution=unresolved` — everything she said hangs on a
 * positive result that is no longer the cycle's current one.
 *
 * `resolutionStatus` is orthogonal to the correction axis: nothing she recorded
 * was corrected away, so the head keeps `recordStatus: "current"`, but the read
 * sends `current: null` because no version still speaks for the cycle's result.
 * The outcome id the versions name is therefore not the one the cycle read
 * carries — a statement can only become unresolved by the result under it being
 * replaced.
 */
export const DEV_PREGNANCY_RESOLUTION_OVERRIDE: "unresolved" | null =
  getDevOutcomeField("dev_pregnancy_resolution", ["unresolved"] as const);

export const DEV_OUTCOME_RESULT_OVERRIDE: DevOutcomeResult | null =
  getDevOutcomeField("dev_outcome_result", [
    "pregnancy",
    "no_pregnancy",
  ] as const);
/**
 * `?dev_cycle_outcome_acknowledged=true|false` stamps `acknowledgedAt` on the
 * closed-cycle fixture's outcome.
 *
 * This is the field the arrival carousel and the acknowledged pause divide on, so
 * it is the one param that shows the real transition. Unlike `dev_outcome_stage`
 * it does not short-circuit the stage hook — the state is still derived from the
 * outcome record, so it exercises the production read path rather than bypassing
 * it.
 */
/**
 * `?dev_cycle_outcome_retracted=true` closes the fixture cycle with no current
 * outcome, which is what the cycle read returns once its only result has been
 * retracted as entered in error: dashboard-api resolves `cycle.outcome` to the
 * single `current` record and sends `null` when there is none, so the retracted
 * row never reaches the client. This is the only shape a retraction can take.
 */
export const DEV_CYCLE_OUTCOME_RETRACTED_OVERRIDE: boolean =
  getDevOutcomeField("dev_cycle_outcome_retracted", ["true"] as const) ===
  "true";

export const DEV_CYCLE_OUTCOME_ACKNOWLEDGED_OVERRIDE: boolean | null = (() => {
  const value = getDevOutcomeField("dev_cycle_outcome_acknowledged", [
    "true",
    "false",
  ] as const);
  return value === null ? null : value === "true";
})();

/**
 * `?dev_cycle_ended_days_ago=60` ages the closed-cycle fixture's ending.
 *
 * The fixture ends one day ago, which is inside every recency window, so the
 * question that matters for a pregnancy — is this still Home's subject once the
 * cycle is long over — had no reachable state to review. A pregnancy lasts
 * months; its Home has to be reviewable months in.
 */
export const DEV_CYCLE_ENDED_DAYS_AGO_OVERRIDE: number | null = (() => {
  if (!__DEV__ || typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get(
    "dev_cycle_ended_days_ago",
  );
  if (!raw) return null;
  const days = Number(raw);
  return Number.isInteger(days) && days >= 0 && days <= 3_650 ? days : null;
})();

export type DevStepState = "completed" | "scheduled" | "skipped" | "outcome";

/**
 * `?dev_stepStates=egg_retrieval:scheduled,pre_treatment:completed` forces
 * per-step panel states so the completed / skipped / scheduled UI can be
 * reviewed without a real cycle in each state. `outcome` leaves egg retrieval
 * pending but gives it a today-date procedure so the outcome handoff can be
 * reviewed too. Dev-only; not persisted (pass it on the cycle-detail URL).
 */
function getDevStepStatesOverride(): Record<string, DevStepState> | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("dev_stepStates");
  if (!raw) return null;
  const map: Record<string, DevStepState> = {};
  for (const pair of raw.split(",")) {
    const [id, state] = pair.split(":").map((s) => s.trim());
    if (
      id &&
      (state === "completed" ||
        state === "scheduled" ||
        state === "skipped" ||
        state === "outcome")
    ) {
      map[id] = state;
    }
  }
  return Object.keys(map).length > 0 ? map : null;
}

export const DEV_STEP_STATES_OVERRIDE = getDevStepStatesOverride();

function daysAgoISO(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

const MOCK_CYCLE_ID = "00000000-0000-0000-0000-00000000c001";
const MOCK_PATIENT_ID = "00000000-0000-0000-0000-0000000000a1";
const MOCK_PRE_TREATMENT_PHASE_ID = "00000000-0000-0000-0000-00000000a001";
const MOCK_PROTOCOL_PHASE_ID = "00000000-0000-0000-0000-00000000a002";
const MOCK_MONITORING_DATE = daysAgoISO(1);

/** Egg-freezing cycle, open, started 10 days ago. */
export const MOCK_ACTIVE_CYCLE: Cycle = {
  id: MOCK_CYCLE_ID,
  patientId: MOCK_PATIENT_ID,
  acceptedTreatmentPlanId: null,
  acceptedTreatmentPlanVersion: null,
  partnerId: null,
  relationshipId: null,
  description: null,
  cycleType: "egg_freezing",
  protocol: "gnrh_antagonist",
  menstruationStartDate: daysAgoISO(10),
  startDate: daysAgoISO(10),
  endDate: null,
  calculatedFertileWindowStart: null,
  calculatedFertileWindowEnd: null,
  status: "open",
  providerId: null,
  ownership: "patient",
  ownerId: MOCK_PATIENT_ID,
  sourceId: null,
  audience: [],
  createdAt: daysAgoISO(10),
  updatedAt: daysAgoISO(0),
  updatedBy: null,
  source: "patient",
  verificationStatus: "unverified",
  verifiedAt: null,
  verifiedBy: null,
  verificationNotes: null,
  rawCycleType: null,
  rawProtocol: null,
  provider: null,
} as Cycle;

export const MOCK_ACTIVE_CYCLE_MISSING_WHERE_WHEN: Cycle = {
  ...MOCK_ACTIVE_CYCLE,
  clinic: null,
  startDate: null,
  menstruationStartDate: null,
} as Cycle;

/** IVF cycle in the two-week-wait state for date-field route QA. */
export const MOCK_WAITING_ACTIVE_CYCLE: Cycle = {
  ...MOCK_ACTIVE_CYCLE,
  cycleType: "ivf",
  startDate: daysAgoISO(20),
  menstruationStartDate: daysAgoISO(20),
} as Cycle;

/** A closed cycle used only by the deterministic post-cycle route fixture. */
export const MOCK_CLOSED_CYCLE: Cycle = {
  ...MOCK_ACTIVE_CYCLE,
  id: "00000000-0000-0000-0000-00000000c002",
  cycleType: DEV_CYCLE_TYPE_OVERRIDE ?? "egg_freezing",
  endDate: daysAgoISO(DEV_CYCLE_ENDED_DAYS_AGO_OVERRIDE ?? 1),
  status: "closed",
  updatedAt: daysAgoISO(0),
  outcome: DEV_CYCLE_OUTCOME_RETRACTED_OVERRIDE
    ? null
    : {
        // Without an outcome id `readCycleEnding` reports `outcomeId: null`, so
        // `shouldUseCycleEndingForHomeExperience` refuses the ending and no
        // post-result Home is reachable from this fixture at all.
        id: "00000000-0000-0000-0000-00000000d002",
        type: DEV_CYCLE_DETAIL_OUTCOME_OVERRIDE ?? "pregnancy",
        resultStatus:
          DEV_OUTCOME_RESULT_OVERRIDE === "no_pregnancy" ||
          DEV_CYCLE_DETAIL_OUTCOME_OVERRIDE === "no_pregnancy"
            ? "verified_negative"
            : "verified_positive",
        source: DEV_OUTCOME_SOURCE_OVERRIDE ?? "patient",
        verificationStatus: DEV_OUTCOME_VERIFICATION_OVERRIDE ?? "verified",
        acknowledgedAt: DEV_CYCLE_OUTCOME_ACKNOWLEDGED_OVERRIDE
          ? daysAgoISO(0)
          : null,
        cancellationReason: null,
        pregnancyOutcome: null,
        miscarriageOutcome: null,
        biochemicalPregnancyObserved: null,
        // The result is dated with the ending it belongs to, so an aged fixture does
        // not describe a result recorded after the cycle it came from.
        dateRecorded: daysAgoISO(DEV_CYCLE_ENDED_DAYS_AGO_OVERRIDE ?? 1),
        resultDate: daysAgoISO(DEV_CYCLE_ENDED_DAYS_AGO_OVERRIDE ?? 1),
        recordedAt: daysAgoISO(DEV_CYCLE_ENDED_DAYS_AGO_OVERRIDE ?? 1),
        weekCount: null,
        liveBirthCount: null,
        notes: null,
      },
} as Cycle;

/**
 * IVF cycle, open, in the post-transfer two-week wait. Started ~30 days ago;
 * the transfer landed 5 days ago and the beta test is 5 days out. Used by the
 * `waiting_beta` scenario so the beta-wait homepage can be reviewed end to end.
 */
export const MOCK_WAITING_CYCLE: Cycle = {
  ...MOCK_ACTIVE_CYCLE,
  cycleType: "ivf",
  menstruationStartDate: daysAgoISO(30),
  startDate: daysAgoISO(30),
  createdAt: daysAgoISO(30),
} as Cycle;

/** Sperm-freezing cycle, open, started 8 days ago, at a named clinic. */
const MOCK_SPERM_FREEZING_CYCLE: Cycle = {
  ...MOCK_ACTIVE_CYCLE,
  cycleType: "sperm_freezing",
  protocol: null,
  provider: "Northstar Fertility",
  menstruationStartDate: null,
  startDate: daysAgoISO(8),
  createdAt: daysAgoISO(8),
} as Cycle;

/** Frozen embryo transfer, open, medicated lining preparation, at a named clinic. */
const MOCK_FET_CYCLE: Cycle = {
  ...MOCK_ACTIVE_CYCLE,
  cycleType: "fet",
  protocol: "hrt",
  provider: "Northstar Fertility",
  menstruationStartDate: daysAgoISO(14),
  startDate: daysAgoISO(14),
  createdAt: daysAgoISO(14),
} as Cycle;

/** IVF, open, antagonist protocol, at a named clinic, started 20 days ago. */
const MOCK_IVF_JOURNEY_CYCLE: Cycle = {
  ...MOCK_ACTIVE_CYCLE,
  cycleType: "ivf",
  protocol: "gnrh_antagonist",
  provider: "Northstar Fertility",
  menstruationStartDate: daysAgoISO(20),
  startDate: daysAgoISO(20),
  createdAt: daysAgoISO(20),
} as Cycle;

/** IUI, open, letrozole stimulation, at a named clinic, started 14 days ago. */
const MOCK_IUI_JOURNEY_CYCLE: Cycle = {
  ...MOCK_ACTIVE_CYCLE,
  cycleType: "iui",
  protocol: "letrozole",
  provider: "Northstar Fertility",
  menstruationStartDate: daysAgoISO(14),
  startDate: daysAgoISO(14),
  createdAt: daysAgoISO(14),
} as Cycle;

const isIvfJourneyScenario =
  DEV_ACTIVE_CYCLE_SCENARIO === "ivf_retrieval_booked" ||
  DEV_ACTIVE_CYCLE_SCENARIO === "ivf_embryo_development" ||
  DEV_ACTIVE_CYCLE_SCENARIO === "ivf_transfer_day" ||
  DEV_ACTIVE_CYCLE_SCENARIO === "ivf_retrieval_day";

const BASE_ACTIVE_CYCLE_REVIEW_MOCK = isIvfJourneyScenario
  ? MOCK_IVF_JOURNEY_CYCLE
  : DEV_ACTIVE_CYCLE_SCENARIO === "iui_insemination_day"
    ? MOCK_IUI_JOURNEY_CYCLE
    : DEV_ACTIVE_CYCLE_SCENARIO === "waiting_phase"
      ? MOCK_WAITING_ACTIVE_CYCLE
      : DEV_ACTIVE_CYCLE_SCENARIO === "waiting_beta"
        ? MOCK_WAITING_CYCLE
        : DEV_ACTIVE_CYCLE_SCENARIO === "missing_where_when"
          ? MOCK_ACTIVE_CYCLE_MISSING_WHERE_WHEN
          : DEV_ACTIVE_CYCLE_SCENARIO === "sperm_results_pending" ||
              DEV_ACTIVE_CYCLE_SCENARIO === "sperm_results_entered" ||
              DEV_ACTIVE_CYCLE_SCENARIO === "sperm_collection_booked" ||
              DEV_ACTIVE_CYCLE_SCENARIO === "sperm_collection_unbooked"
            ? MOCK_SPERM_FREEZING_CYCLE
            : DEV_ACTIVE_CYCLE_SCENARIO === "fet_lining_prep" ||
                DEV_ACTIVE_CYCLE_SCENARIO === "fet_transfer_unbooked"
              ? MOCK_FET_CYCLE
              : MOCK_ACTIVE_CYCLE;

// `?dev_cycleType=` reshapes the resolved mock so any cycle type's step list
// can be reviewed on cycle-detail. Only the cycleType changes; phases stay
// generic (enough to render the tabs/states, not full per-type data).
export const ACTIVE_CYCLE_REVIEW_MOCK: Cycle = DEV_CYCLE_TYPE_OVERRIDE
  ? ({
      ...BASE_ACTIVE_CYCLE_REVIEW_MOCK,
      cycleType: DEV_CYCLE_TYPE_OVERRIDE,
      ...(DEV_CARE_CONNECTIONS_CYCLE
        ? {
            providerId: "81000000-0000-4000-8000-000000000001",
            provider: "Northstar Fertility",
          }
        : {}),
      ...(DEV_OUTCOME_SOURCE_OVERRIDE || DEV_OUTCOME_VERIFICATION_OVERRIDE
        ? {
            outcome: {
              id: "00000000-0000-0000-0000-00000000c003",
              cycleId: MOCK_CYCLE_ID,
              type: DEV_PREGNANCY_STANDS_OVERRIDE
                ? "pregnancy"
                : (DEV_OUTCOME_RESULT_OVERRIDE ?? "no_pregnancy"),
              resultStatus:
                DEV_PREGNANCY_STANDS_OVERRIDE ||
                DEV_OUTCOME_RESULT_OVERRIDE === "pregnancy"
                  ? "positive"
                  : "negative",
              cancellationReason: null,
              pregnancyOutcome: null,
              miscarriageOutcome: null,
              biochemicalPregnancyObserved: null,
              dateRecorded: daysAgoISO(1),
              weekCount: null,
              liveBirthCount: null,
              createdAt: daysAgoISO(1),
              updatedAt: daysAgoISO(0),
              updatedBy: null,
              source: DEV_OUTCOME_SOURCE_OVERRIDE ?? "patient",
              verificationStatus:
                DEV_OUTCOME_VERIFICATION_OVERRIDE ?? "unverified",
              verifiedAt: null,
              verifiedBy: null,
              verificationNotes: null,
              notes: null,
            },
          }
        : {}),
    } as Cycle)
  : ({
      ...BASE_ACTIVE_CYCLE_REVIEW_MOCK,
      ...(DEV_CARE_CONNECTIONS_CYCLE
        ? {
            providerId: "81000000-0000-4000-8000-000000000001",
            provider: "Northstar Fertility",
          }
        : {}),
    } as Cycle);

/** Two phases: pre-treatment completed, protocol (stim) active on day 7. */
const MOCK_DEFAULT_CYCLE_PHASES: Phase[] = [
  {
    id: MOCK_PRE_TREATMENT_PHASE_ID,
    cycleId: MOCK_CYCLE_ID,
    type: "pre_treatment",
    startDate: daysAgoISO(10),
    startTime: null,
    endDate: daysAgoISO(7),
    notes: null,
    createdAt: daysAgoISO(10),
    updatedAt: daysAgoISO(7),
    updatedBy: null,
    source: "patient",
    verificationStatus: "unverified",
    verifiedAt: null,
    verifiedBy: null,
    verificationNotes: null,
  } as Phase,
  {
    id: MOCK_PROTOCOL_PHASE_ID,
    cycleId: MOCK_CYCLE_ID,
    type: "protocol",
    startDate: daysAgoISO(6),
    startTime: null,
    endDate: null,
    notes: null,
    createdAt: daysAgoISO(6),
    updatedAt: daysAgoISO(0),
    updatedBy: null,
    source: "patient",
    verificationStatus: "unverified",
    verifiedAt: null,
    verifiedBy: null,
    verificationNotes: null,
    ovaryFollicles: [
      ...[8, 9, 10, 11, 12, 13].map((size, index) => ({
        id: `00000000-0000-0000-0000-00000000f0${index}`,
        patientId: MOCK_PATIENT_ID,
        phaseId: MOCK_PROTOCOL_PHASE_ID,
        ovarySide: index < 3 ? "left" : "right",
        date: MOCK_MONITORING_DATE,
        count: 1,
        size,
        clinicalAssessment: "normal",
        clinicalAssessmentDetails: null,
        createdAt: MOCK_MONITORING_DATE,
        updatedAt: MOCK_MONITORING_DATE,
        updatedBy: null,
        source: "provider",
        verificationStatus: "verified",
        verifiedAt: null,
        verifiedBy: null,
        verificationNotes: null,
      })),
      ...[14, 15, 15, 16, 17, 18].map((size, index) => ({
        id: `00000000-0000-0000-0000-00000000f1${index}`,
        patientId: MOCK_PATIENT_ID,
        phaseId: MOCK_PROTOCOL_PHASE_ID,
        ovarySide: index < 3 ? "left" : "right",
        date: MOCK_MONITORING_DATE,
        count: 1,
        size,
        clinicalAssessment: "normal",
        clinicalAssessmentDetails: null,
        createdAt: MOCK_MONITORING_DATE,
        updatedAt: MOCK_MONITORING_DATE,
        updatedBy: null,
        source: "provider",
        verificationStatus: "verified",
        verifiedAt: null,
        verifiedBy: null,
        verificationNotes: null,
      })),
    ],
    testResults: [
      {
        id: "00000000-0000-0000-0000-00000000e201",
        patientId: MOCK_PATIENT_ID,
        phaseId: MOCK_PROTOCOL_PHASE_ID,
        cycleId: MOCK_CYCLE_ID,
        name: "Estradiol",
        type: "blood",
        bloodTestCategory: "hormonal",
        doctorValue: "820",
        patientValue: null,
        value: "820",
        valueType: "numeric",
        unit: "pg/mL",
        date: MOCK_MONITORING_DATE,
        notes: null,
        clinicalAssessment: "normal",
        clinicalAssessmentDetails: null,
        createdAt: MOCK_MONITORING_DATE,
        updatedAt: MOCK_MONITORING_DATE,
        updatedBy: null,
        source: "provider",
        verificationStatus: "verified",
        verifiedAt: null,
        verifiedBy: null,
        verificationNotes: null,
      },
    ],
    fertilityMetrics: [
      {
        id: "00000000-0000-0000-0000-00000000ed01",
        phaseId: MOCK_PROTOCOL_PHASE_ID,
        type: "endometrial_thickness",
        value: 7.2,
        createdAt: MOCK_MONITORING_DATE,
        updatedAt: MOCK_MONITORING_DATE,
        updatedBy: null,
        source: "provider",
        verificationStatus: "verified",
        verifiedAt: null,
        verifiedBy: null,
        verificationNotes: null,
      },
    ],
  } as Phase,
];

const MOCK_RETRIEVAL_TRIGGER_SHOT: NonNullable<Phase["triggerShot"]> = {
  id: "00000000-0000-0000-0000-00000000a009",
  phaseId: MOCK_PROTOCOL_PHASE_ID,
  medicationId: null,
  date: daysAgoISO(3),
  time: "21:00",
  dose: null,
  doseUnit: null,
  route: null,
  notes: null,
  createdAt: daysAgoISO(3),
  updatedAt: daysAgoISO(3),
  updatedBy: null,
  source: "patient",
  verificationStatus: "unverified",
  verifiedAt: null,
  verifiedBy: null,
  verificationNotes: null,
};

/** Egg retrieval started two days ago and has no result recorded yet. */
const MOCK_RETRIEVAL_PENDING_CYCLE_PHASES: Phase[] = [
  {
    ...MOCK_DEFAULT_CYCLE_PHASES[0]!,
  },
  {
    ...MOCK_DEFAULT_CYCLE_PHASES[1]!,
    endDate: daysAgoISO(4),
    triggerShot: MOCK_RETRIEVAL_TRIGGER_SHOT,
    ovaryFollicles: undefined,
    testResults: undefined,
    fertilityMetrics: undefined,
  },
  {
    id: "00000000-0000-0000-0000-00000000a008",
    cycleId: MOCK_CYCLE_ID,
    type: "egg_retrieval",
    startDate: daysAgoISO(2),
    startTime: "09:00",
    endDate: null,
    notes: null,
    createdAt: daysAgoISO(2),
    updatedAt: daysAgoISO(0),
    updatedBy: null,
    source: "patient",
    verificationStatus: "unverified",
    verifiedAt: null,
    verifiedBy: null,
    verificationNotes: null,
  } as Phase,
];

const MOCK_LUTEAL_PHASE_ID = "00000000-0000-0000-0000-00000000a007";

/**
 * Post-transfer two-week-wait phases. Every step from pre-treatment through
 * transfer is completed (endDate strictly after startDate); the luteal phase
 * is open with a startDate = transfer date (5 days ago) and endDate = beta
 * test date (5 days out). That shape mirrors what Cycle Detail's waiting view
 * expects, and lets `resolveCycleHeaderState` land on beta_scheduled.
 */
const WAITING_DAYS_AFTER_TRANSFER =
  DEV_WAIT_DAYS_AFTER_TRANSFER_OVERRIDE === null
    ? 5
    : DEV_WAIT_DAYS_AFTER_TRANSFER_OVERRIDE + 1;
const WAITING_BETA_TOTAL_DAYS =
  DEV_WAIT_DAYS_AFTER_TRANSFER_OVERRIDE === null ? 10 : 14;
const MOCK_WAITING_CYCLE_PHASES: Phase[] = [
  { type: "pre_treatment", startAgo: 30, endAgo: 26 },
  { type: "protocol", startAgo: 26, endAgo: 15 },
  { type: "egg_retrieval", startAgo: 12, endAgo: 11 },
  { type: "fertilization", startAgo: 11, endAgo: 10 },
  { type: "embryo_development", startAgo: 10, endAgo: 5 },
  { type: "transfer", startAgo: 5, endAgo: 4 },
  // Luteal open: startDate 5 days ago (transfer), endDate 5 days out (beta).
  {
    type: "luteal_phase",
    startAgo: WAITING_DAYS_AFTER_TRANSFER,
    endAgo: WAITING_DAYS_AFTER_TRANSFER - WAITING_BETA_TOTAL_DAYS,
  },
].map(
  ({ type, startAgo, endAgo }, index) =>
    ({
      id:
        type === "luteal_phase"
          ? MOCK_LUTEAL_PHASE_ID
          : `00000000-0000-0000-0000-00000000b00${index}`,
      cycleId: MOCK_CYCLE_ID,
      type,
      startDate: daysAgoISO(startAgo),
      startTime: null,
      endDate:
        type === "luteal_phase" &&
        DEV_ACTIVE_CYCLE_SCENARIO === "waiting_beta_pending"
          ? null
          : daysAgoISO(endAgo),
      notes: null,
      createdAt: daysAgoISO(startAgo),
      updatedAt: daysAgoISO(0),
      updatedBy: null,
      source: "patient",
      verificationStatus: "unverified",
      verifiedAt: null,
      verifiedBy: null,
      verificationNotes: null,
    }) as Phase,
);

const MOCK_WAITING_PHASES: Phase[] = [
  {
    id: "00000000-0000-0000-0000-00000000a003",
    cycleId: MOCK_CYCLE_ID,
    type: "transfer",
    startDate: daysAgoISO(3),
    startTime: null,
    endDate: daysAgoISO(2),
    notes: null,
    createdAt: daysAgoISO(3),
    updatedAt: daysAgoISO(3),
    updatedBy: null,
    source: "patient",
    verificationStatus: "unverified",
    verifiedAt: null,
    verifiedBy: null,
    verificationNotes: null,
  } as Phase,
  {
    id: "00000000-0000-0000-0000-00000000a004",
    cycleId: MOCK_CYCLE_ID,
    type: "luteal_phase",
    startDate: daysAgoISO(3),
    startTime: null,
    endDate: daysAgoISO(-11),
    notes: null,
    createdAt: daysAgoISO(3),
    updatedAt: daysAgoISO(0),
    updatedBy: null,
    source: "patient",
    verificationStatus: "unverified",
    verifiedAt: null,
    verifiedBy: null,
    verificationNotes: null,
  } as Phase,
];

const MOCK_SPERM_COLLECTION_PHASE_ID = "00000000-0000-0000-0000-00000000a010";

type SpermFixtureFigures = Pick<
  NonNullable<Phase["spermiograms"]>[number],
  | "semenVolume"
  | "volumeUnit"
  | "spermConcentration"
  | "progressiveMotility"
  | "normalMorphology"
>;

/**
 * Sperm-freezing phases as the treatment hub stores them: screening as
 * `protocol` and the analysis as `fertilization` (`STEP_TO_PHASE_TYPE`). A
 * completed collection carries the spermiogram row the real save writes, with
 * no figures, so the wait is read from the steps and not from the row. An
 * analysis step given `figures` carries its own row with them, as her save on
 * that step writes it.
 */
function buildSpermFixturePhases(
  steps: {
    type: string;
    startAgo: number;
    endAgo: number | null;
    figures?: SpermFixtureFigures;
  }[],
): Phase[] {
  return steps.map(
    ({ type, startAgo, endAgo, figures }, index) =>
      ({
        id:
          type === "sperm_collection"
            ? MOCK_SPERM_COLLECTION_PHASE_ID
            : `00000000-0000-0000-0000-00000000d00${index}`,
        cycleId: MOCK_CYCLE_ID,
        type,
        startDate: daysAgoISO(startAgo),
        startTime: null,
        endDate: endAgo === null ? null : daysAgoISO(endAgo),
        notes: null,
        createdAt: daysAgoISO(8),
        updatedAt: daysAgoISO(1),
        updatedBy: null,
        source: "patient",
        verificationStatus: "unverified",
        verifiedAt: null,
        verifiedBy: null,
        verificationNotes: null,
        spermiograms: figures
          ? [
              {
                id: "00000000-0000-0000-0000-00000000d102",
                patientId: MOCK_PATIENT_ID,
                phaseId: `00000000-0000-0000-0000-00000000d00${index}`,
                date: daysAgoISO(startAgo),
                source: "patient",
                createdAt: daysAgoISO(startAgo),
                spermCount: null,
                tmsc: null,
                motility: null,
                ...figures,
              },
            ]
          : type === "sperm_collection" && endAgo !== null
            ? [
                {
                  id: "00000000-0000-0000-0000-00000000d101",
                  patientId: MOCK_PATIENT_ID,
                  phaseId: MOCK_SPERM_COLLECTION_PHASE_ID,
                  date: daysAgoISO(startAgo),
                  source: "patient",
                  spermCount: null,
                  spermConcentration: null,
                  semenVolume: null,
                  tmsc: null,
                  motility: null,
                  progressiveMotility: null,
                  normalMorphology: null,
                },
              ]
            : undefined,
      }) as Phase,
  );
}

/**
 * Pre-treatment and screening done, the sample given two days ago, and the
 * analysis booked for two days out.
 */
const MOCK_SPERM_RESULTS_PENDING_PHASES = buildSpermFixturePhases([
  { type: "pre_treatment", startAgo: 8, endAgo: 7 },
  { type: "protocol", startAgo: 6, endAgo: 4 },
  { type: "sperm_collection", startAgo: 2, endAgo: 1 },
  { type: "fertilization", startAgo: -2, endAgo: null },
]);

/**
 * The same steps once the analysis is in: she typed the figures from the
 * report on the analysis step yesterday. The cycle stays open for the freezing.
 */
const MOCK_SPERM_RESULTS_ENTERED_PHASES = buildSpermFixturePhases([
  { type: "pre_treatment", startAgo: 8, endAgo: 7 },
  { type: "protocol", startAgo: 6, endAgo: 4 },
  { type: "sperm_collection", startAgo: 3, endAgo: 2 },
  {
    type: "fertilization",
    startAgo: 1,
    endAgo: 0,
    figures: {
      semenVolume: 3.2,
      volumeUnit: "mL",
      spermConcentration: 48,
      progressiveMotility: 42,
      normalMorphology: 6,
    },
  },
]);

/** Pre-treatment done, screening booked for tomorrow, collection in 3 days. */
const MOCK_SPERM_COLLECTION_BOOKED_PHASES = buildSpermFixturePhases([
  { type: "pre_treatment", startAgo: 9, endAgo: 8 },
  { type: "protocol", startAgo: -1, endAgo: null },
  { type: "sperm_collection", startAgo: -3, endAgo: null },
]);

/** Pre-treatment done; no screening or collection recorded yet. */
const MOCK_SPERM_COLLECTION_UNBOOKED_PHASES = buildSpermFixturePhases([
  { type: "pre_treatment", startAgo: 9, endAgo: 8 },
]);

const MOCK_FET_PROTOCOL_PHASE_ID = "00000000-0000-0000-0000-00000000f010";

/** One lining scan as the clinic records it: an imaging result on the phase. */
function fetLiningScan(daysAgo: number, mm: number, index: number) {
  return {
    id: `00000000-0000-0000-0000-00000000f1${index}0`,
    patientId: MOCK_PATIENT_ID,
    phaseId: MOCK_FET_PROTOCOL_PHASE_ID,
    cycleId: MOCK_CYCLE_ID,
    name: "Endometrial thickness",
    type: "imaging",
    doctorValue: String(mm),
    patientValue: null,
    value: String(mm),
    valueType: "numeric",
    unit: "mm",
    date: daysAgoISO(daysAgo),
    notes: null,
    clinicalAssessment: "normal",
    clinicalAssessmentDetails: null,
    createdAt: daysAgoISO(daysAgo),
    updatedAt: daysAgoISO(daysAgo),
    updatedBy: null,
    source: "provider",
    verificationStatus: "verified",
    verifiedAt: null,
    verifiedBy: null,
    verificationNotes: null,
  };
}

function fetPhase(
  id: string,
  type: string,
  startAgo: number,
  endAgo: number | null,
  extra: Partial<Phase> = {},
): Phase {
  return {
    id,
    cycleId: MOCK_CYCLE_ID,
    type,
    startDate: daysAgoISO(startAgo),
    startTime: null,
    endDate: endAgo === null ? null : daysAgoISO(endAgo),
    notes: null,
    createdAt: daysAgoISO(14),
    updatedAt: daysAgoISO(1),
    updatedBy: null,
    source: "patient",
    verificationStatus: "unverified",
    verifiedAt: null,
    verifiedBy: null,
    verificationNotes: null,
    ...extra,
  } as Phase;
}

/**
 * Pre-treatment done, lining preparation in its tenth day with three lining
 * scans (6.4, 7.8 and 8.9 mm), and the transfer booked four days out.
 */
const MOCK_FET_LINING_PREP_PHASES: Phase[] = [
  fetPhase("00000000-0000-0000-0000-00000000f001", "pre_treatment", 14, 13),
  fetPhase(MOCK_FET_PROTOCOL_PHASE_ID, "protocol", 9, null, {
    testResults: [
      fetLiningScan(6, 6.4, 1),
      fetLiningScan(3, 7.8, 2),
      fetLiningScan(1, 8.9, 3),
    ],
  } as Partial<Phase>),
  fetPhase("00000000-0000-0000-0000-00000000f003", "transfer", -4, null),
];

/** The same preparation with no transfer date recorded yet. */
const MOCK_FET_TRANSFER_UNBOOKED_PHASES: Phase[] =
  MOCK_FET_LINING_PREP_PHASES.filter((phase) => phase.type !== "transfer");

/** A clinic-recorded lab count on a phase (retrieved, fertilized, embryos). */
function ivfMetric(
  phaseId: string,
  type: string,
  value: number,
  daysAgo: number,
) {
  return {
    id: `${phaseId.slice(0, -4)}${type.length.toString().padStart(2, "0")}${String(value).padStart(2, "0")}`,
    phaseId,
    type,
    value,
    createdAt: daysAgoISO(daysAgo),
    updatedAt: daysAgoISO(daysAgo),
    updatedBy: null,
    source: "provider",
    verificationStatus: "verified",
    verifiedAt: null,
    verifiedBy: null,
    verificationNotes: null,
  };
}

const IVF_IDS = {
  pre: "00000000-0000-0000-0000-000000009001",
  protocol: "00000000-0000-0000-0000-000000009002",
  retrieval: "00000000-0000-0000-0000-000000009003",
  fertilization: "00000000-0000-0000-0000-000000009004",
  embryo: "00000000-0000-0000-0000-000000009005",
  transfer: "00000000-0000-0000-0000-000000009006",
};

const IUI_IDS = {
  pre: "00000000-0000-0000-0000-000000009101",
  protocol: "00000000-0000-0000-0000-000000009102",
  sperm: "00000000-0000-0000-0000-000000009103",
  insemination: "00000000-0000-0000-0000-000000009104",
};

/** The stimulation phase ending on trigger day, with the trigger logged. */
function ivfStimulation(startAgo: number, triggerAgo: number): Phase {
  return fetPhase(IVF_IDS.protocol, "protocol", startAgo, triggerAgo, {
    triggerShot: {
      ...MOCK_RETRIEVAL_TRIGGER_SHOT,
      phaseId: IVF_IDS.protocol,
      date: daysAgoISO(triggerAgo),
      time: "21:00",
    },
  } as Partial<Phase>);
}

/** Retrieval and fertilization done, with the lab's counts recorded. */
function ivfLabDone(retrievalAgo: number): Phase[] {
  return [
    fetPhase(
      IVF_IDS.retrieval,
      "egg_retrieval",
      retrievalAgo,
      retrievalAgo - 1,
      {
        fertilityMetrics: [
          ivfMetric(IVF_IDS.retrieval, "retrieved_oocytes", 12, retrievalAgo),
          ivfMetric(IVF_IDS.retrieval, "injected_oocytes", 9, retrievalAgo),
        ],
      } as Partial<Phase>,
    ),
    fetPhase(
      IVF_IDS.fertilization,
      "fertilization",
      retrievalAgo,
      retrievalAgo - 1,
      {
        fertilityMetrics: [
          ivfMetric(
            IVF_IDS.fertilization,
            "fertilized_oocytes",
            7,
            retrievalAgo - 1,
          ),
        ],
      } as Partial<Phase>,
    ),
  ];
}

/**
 * The IVF journey between stimulation and the wait, each a day in a real
 * cycle: 12 eggs retrieved, 9 mature, 7 fertilized, 4 blastocysts. Plus IUI
 * insemination day, which has the same shape: one dated procedure step.
 */
const IVF_JOURNEY_PHASES: Partial<Record<DevActiveCycleScenario, Phase[]>> = {
  // Trigger last night, retrieval booked for tomorrow.
  ivf_retrieval_booked: [
    fetPhase(IVF_IDS.pre, "pre_treatment", 20, 19),
    ivfStimulation(12, 1),
    fetPhase(IVF_IDS.retrieval, "egg_retrieval", -1, null),
  ],
  // Retrieval three days ago; embryos developing, transfer in two days.
  ivf_embryo_development: [
    fetPhase(IVF_IDS.pre, "pre_treatment", 20, 19),
    ivfStimulation(15, 5),
    ...ivfLabDone(3),
    fetPhase(IVF_IDS.embryo, "embryo_development", 2, null),
    fetPhase(IVF_IDS.transfer, "transfer", -2, null),
  ],
  // Day-5 transfer today.
  ivf_transfer_day: [
    fetPhase(IVF_IDS.pre, "pre_treatment", 20, 19),
    ivfStimulation(17, 7),
    ...ivfLabDone(5),
    fetPhase(IVF_IDS.embryo, "embryo_development", 4, 1, {
      fertilityMetrics: [
        ivfMetric(IVF_IDS.embryo, "embryos_day_3", 6, 2),
        ivfMetric(IVF_IDS.embryo, "embryos_day_5", 4, 1),
      ],
    } as Partial<Phase>),
    fetPhase(IVF_IDS.transfer, "transfer", 0, null),
  ],
  // Trigger two nights ago, retrieval this morning, nothing recorded yet.
  ivf_retrieval_day: [
    fetPhase(IVF_IDS.pre, "pre_treatment", 20, 19),
    ivfStimulation(13, 2),
    fetPhase(IVF_IDS.retrieval, "egg_retrieval", 0, null),
  ],
  // IUI: letrozole stimulation with the trigger last night, the sample given,
  // and the insemination booked for today.
  iui_insemination_day: [
    fetPhase(IUI_IDS.pre, "pre_treatment", 14, 13),
    fetPhase(IUI_IDS.protocol, "protocol", 12, 1, {
      triggerShot: {
        ...MOCK_RETRIEVAL_TRIGGER_SHOT,
        phaseId: IUI_IDS.protocol,
        date: daysAgoISO(1),
        time: "21:00",
      },
    } as Partial<Phase>),
    fetPhase(IUI_IDS.sperm, "sperm_collection", 1, 0),
    fetPhase(IUI_IDS.insemination, "insemination", 0, null),
  ],
};

const BASE_CYCLE_PHASES: Phase[] =
  DEV_ACTIVE_CYCLE_SCENARIO === "retrieval_pending"
    ? MOCK_RETRIEVAL_PENDING_CYCLE_PHASES
    : DEV_ACTIVE_CYCLE_SCENARIO === "waiting_beta" ||
        DEV_ACTIVE_CYCLE_SCENARIO === "waiting_beta_pending"
      ? MOCK_WAITING_CYCLE_PHASES
      : DEV_ACTIVE_CYCLE_SCENARIO === "waiting_phase"
        ? MOCK_WAITING_PHASES
        : DEV_ACTIVE_CYCLE_SCENARIO === "sperm_results_pending"
          ? MOCK_SPERM_RESULTS_PENDING_PHASES
          : DEV_ACTIVE_CYCLE_SCENARIO === "sperm_results_entered"
            ? MOCK_SPERM_RESULTS_ENTERED_PHASES
            : DEV_ACTIVE_CYCLE_SCENARIO === "sperm_collection_booked"
              ? MOCK_SPERM_COLLECTION_BOOKED_PHASES
              : DEV_ACTIVE_CYCLE_SCENARIO === "sperm_collection_unbooked"
                ? MOCK_SPERM_COLLECTION_UNBOOKED_PHASES
                : DEV_ACTIVE_CYCLE_SCENARIO === "fet_lining_prep"
                  ? MOCK_FET_LINING_PREP_PHASES
                  : DEV_ACTIVE_CYCLE_SCENARIO === "fet_transfer_unbooked"
                    ? MOCK_FET_TRANSFER_UNBOOKED_PHASES
                    : MOCK_DEFAULT_CYCLE_PHASES;

export const MOCK_CYCLE_PHASES: Phase[] =
  IVF_JOURNEY_PHASES[DEV_ACTIVE_CYCLE_SCENARIO] ?? BASE_CYCLE_PHASES;

export { MOCK_CYCLE_ID };

const PREGNANCY_STATUS_HEAD_ID = "00000000-0000-4000-8000-00000000c004";
const PREGNANCY_STATUS_CORRECTED_ID = "00000000-0000-4000-8000-00000000c005";
/**
 * The positive result an unresolved statement still names. It is deliberately
 * not the id the cycle read carries: a statement only becomes unresolved when
 * the result it hangs on stops being the cycle's current positive one.
 */
const PREGNANCY_STATUS_REPLACED_OUTCOME_ID =
  "00000000-0000-4000-8000-00000000c006";

/** The existing capture cycle's separate patient statement; never used for real cycle ids. */
export function readDevCyclePregnancyStatus(
  cycleId: string,
): CyclePregnancyStatusAggregate | undefined {
  if (!__DEV__) return undefined;
  const onActiveFixture =
    DEV_ACTIVE_CYCLE_OVERRIDE && cycleId === ACTIVE_CYCLE_REVIEW_MOCK.id;
  /**
   * The closed-cycle fixture answers the same read, because Past Cycle Detail
   * calls the same hook with the id in its path. It only answers once a stand
   * is asked for, so every past-cycle route that never mentions a pregnancy
   * keeps falling through to the API exactly as before.
   */
  const onClosedFixture =
    Boolean(DEV_CYCLE_DETAIL_OUTCOME_OVERRIDE) &&
    cycleId === MOCK_CLOSED_CYCLE.id &&
    DEV_PREGNANCY_STANDS_OVERRIDE !== null;
  if (!onActiveFixture && !onClosedFixture) return undefined;
  const status = DEV_PREGNANCY_STANDS_OVERRIDE;
  const cycle = onActiveFixture ? ACTIVE_CYCLE_REVIEW_MOCK : MOCK_CLOSED_CYCLE;
  const outcome = cycle.outcome;
  if (!status || !outcome?.id) return { current: null, history: [] };
  const corrected = DEV_PREGNANCY_HISTORY_OVERRIDE === "corrected";
  const unresolved = DEV_PREGNANCY_RESOLUTION_OVERRIDE === "unresolved";
  const resolutionStatus = unresolved ? "unresolved" : "current";
  const outcomeId = unresolved
    ? PREGNANCY_STATUS_REPLACED_OUTCOME_ID
    : outcome.id;
  /**
   * The history prints each recording instant down to the minute, so a wall
   * clock would render the same state differently a minute later. The fixture
   * pins the time of day and keeps the dates relative to today, which is what
   * every other date in this file does.
   */
  const recordedAt = (days: number, timeOfDay: string) =>
    `${daysAgoISO(days)}T${timeOfDay}`;
  const head: CyclePregnancyStatusRecord = {
    id: PREGNANCY_STATUS_HEAD_ID,
    patientId: cycle.patientId,
    cycleId,
    outcomeId,
    status,
    recordStatus: "current",
    resolutionStatus,
    reportedDate:
      status === "continuing" ? toLocalDateString(new Date()) : null,
    reportedDatePrecision: status === "continuing" ? "day" : null,
    /**
     * Only the corrected head dates the event, and it dates it to a month. A
     * day-precision date reads the same as a plain date, so the month is the
     * only value that shows `eventDatePrecision` doing anything at all.
     */
    eventDate:
      corrected && status !== "continuing"
        ? `${daysAgoISO(21).slice(0, 7)}-01`
        : null,
    eventDatePrecision: corrected && status !== "continuing" ? "month" : null,
    gestationalWeek: DEV_PREGNANCY_WEEKS_OVERRIDE,
    lossType: status === "ended" ? "complete_miscarriage" : null,
    birthType:
      status === "birth"
        ? DEV_PREGNANCY_BABIES_OVERRIDE && DEV_PREGNANCY_BABIES_OVERRIDE > 1
          ? "multiple_live_birth"
          : "live_birth"
        : null,
    liveBirthCount: status === "birth" ? DEV_PREGNANCY_BABIES_OVERRIDE : null,
    supersedesStatusId: corrected ? PREGNANCY_STATUS_CORRECTED_ID : null,
    correctionReason: corrected
      ? "My clinic told me the pregnancy had already ended."
      : null,
    clientRequestId: null,
    recordedAt: recordedAt(corrected ? 2 : 0, "09:12:00.000Z"),
    ownership: "patient",
    ownerId: cycle.patientId,
    source: "patient",
    sourceId: cycle.patientId,
    verificationStatus: "unverified",
    audience: [],
  };
  if (!corrected) return { current: unresolved ? null : head, history: [head] };
  /**
   * What she said first, as the read sends it back: the API appends, so the
   * record it already held keeps every field it was written with and only its
   * `recordStatus` moves to `corrected`. Its `resolutionStatus` follows the
   * outcome, not the correction — both versions hang on the same result.
   */
  const superseded: CyclePregnancyStatusRecord = {
    ...head,
    id: PREGNANCY_STATUS_CORRECTED_ID,
    status: "continuing",
    recordStatus: "corrected",
    reportedDate: daysAgoISO(9),
    reportedDatePrecision: "day",
    eventDate: null,
    eventDatePrecision: null,
    gestationalWeek: DEV_PREGNANCY_WEEKS_OVERRIDE ?? 7,
    lossType: null,
    birthType: null,
    liveBirthCount: null,
    supersedesStatusId: null,
    correctionReason: null,
    recordedAt: recordedAt(9, "08:40:00.000Z"),
  };
  return {
    current: unresolved ? null : head,
    history: [superseded, head],
  };
}
